import * as d3 from 'd3';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import Animated, { ZoomIn } from 'react-native-reanimated';
import { View, YStack } from 'smoothie';

import { convertCategoryToIcon } from '../../../common/utils/billUtils';
import { selectedBillCategoriesAtom } from '../../state/BillsAtoms';
import {
    type BillCategory,
    BillCategoryBackgroundColors,
    BillCategoryTextColors,
} from '../../types/Bill';
import { BillIcon, BillIconSize } from '../common/BillIcons';

const STARTING_ANIMATION_DELAY = 50;
const RADIUS_ADJUSTMENT = 8;
const MINIMUM_CATEGORY_PERCENTAGE = 0.05;
const MAX_NUMBER_OF_CATEGORIES = 6;

interface NodeData {
    adjustedValue: number;
}

// takes the bill categories data and returns size & coordinates info in order to render the bubbles
function pack(data: NodeData[], width: number, height: number) {
    return d3.pack().size([width, height]).padding(32)(
        /* eslint-disable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-unsafe-return */
        // @ts-expect-error
        d3.hierarchy({ children: data }).sum((d) => d.adjustedValue),
        /* eslint-enable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-unsafe-return */
    );
}

interface BillCategoryBubbleProps {
    x: number; // left
    y: number; // top
    r: number; // radius
    name: string;
    value: number;
    index: number;
}

const BillCategoryBubble = ({
    x,
    y,
    r,
    name,
    index,
}: BillCategoryBubbleProps) => {
    const [selectedBillCategories, setSelectedBillCategories] = useAtom(
        selectedBillCategoriesAtom,
    );

    const icon = convertCategoryToIcon(name);
    const isCategorySelected = selectedBillCategories.includes(name);

    return (
        <Animated.View
            entering={ZoomIn.springify()
                .damping(10)
                .stiffness(93)
                .delay(STARTING_ANIMATION_DELAY * index)}
            key={name}
            style={{
                position: 'absolute',
                top: y - RADIUS_ADJUSTMENT,
                left: x - RADIUS_ADJUSTMENT,
                width: r * 2,
                height: r * 2,
            }}
        >
            <View
                width="100%"
                height="100%"
                borderRadius={r}
                backgroundColor={
                    BillCategoryBackgroundColors[name as BillCategory]
                }
                alignItems="center"
                justifyContent="center"
                animation="button"
                pressStyle={{
                    scale: 1.05,
                }}
                style={{
                    borderWidth: 2,
                    borderColor: isCategorySelected
                        ? BillCategoryTextColors[name as BillCategory]
                        : BillCategoryBackgroundColors[name as BillCategory],
                }}
                onPress={() => {
                    if (
                        selectedBillCategories.length === 1 &&
                        selectedBillCategories[0] === name
                    ) {
                        setSelectedBillCategories([]);
                    } else {
                        setSelectedBillCategories([name]);
                    }
                }}
            >
                <YStack alignItems="center">
                    <BillIcon
                        icon={icon}
                        size={BillIconSize.MEDIUM}
                        iconColor={BillCategoryTextColors[name as BillCategory]}
                        color="transparent"
                    />
                </YStack>
            </View>
        </Animated.View>
    );
};

interface BillBubblesProps {
    billAmountsByCategory: Record<string, number>;
    containerHeight: number;
}

const BillBubbles = ({
    billAmountsByCategory,
    containerHeight,
}: BillBubblesProps) => {
    const { width: containerWidth } = useWindowDimensions();
    const [bubbleData, setBubbleData] = useState<BillCategoryBubbleProps[]>([]);

    const total = useMemo(() => {
        return Object.values(billAmountsByCategory).reduce(
            (acc, amount) => acc + amount,
            0,
        );
    }, [billAmountsByCategory]);

    // sort the categories by amount, descending
    const sortedCategories = useMemo(() => {
        const amounts = Object.entries(billAmountsByCategory).slice(
            0,
            MAX_NUMBER_OF_CATEGORIES,
        );
        amounts.sort((a, b) => b[1] - a[1]);

        return amounts.map(([name, value]) => {
            // in order to ensure no bubbles are too small, we'll bump any amounts smaller than 5% of the total to be at 5%
            // we'll still show the correct amount in the bubble's label, this adjustment is just for d3
            const adjustedValue =
                value / total < MINIMUM_CATEGORY_PERCENTAGE
                    ? total * MINIMUM_CATEGORY_PERCENTAGE
                    : value;

            return {
                name,
                value,
                adjustedValue,
            };
        });
    }, [billAmountsByCategory]);

    useEffect(() => {
        if (sortedCategories.length) {
            const leaves = pack(
                sortedCategories,
                containerWidth,
                containerHeight,
            ).leaves();

            const newBubbleData = leaves.map((leaf, index) => {
                const { x, y, r, data } = leaf;
                const { name, value } = data as { name: string; value: number };

                return {
                    x: x - r,
                    y: y - r,
                    r: r + RADIUS_ADJUSTMENT,
                    name,
                    value,
                    index,
                };
            });
            setBubbleData(newBubbleData);
        }
    }, [sortedCategories]);

    return (
        <View
            flex={1}
            width={containerWidth}
            height={containerHeight}
            position="relative"
        >
            {bubbleData.map((data, index) => (
                <BillCategoryBubble key={index} {...data} />
            ))}
        </View>
    );
};

export default BillBubbles;
